import { TextField } from '@automata/ui';
import { useFlag } from '@automata/utils/hooks';
import CircleIcon from '@mui/icons-material/Circle';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import FormControl from '@mui/material/FormControl';
import Stack from '@mui/material/Stack';
import { useCallback, useMemo, useState } from 'react';

import { GrippingDimensions } from './LabwareFields/GrippingDimensions';
import {
  getAllLabwareDefinitionsOfType,
  getLabwareURI,
} from '../../../labware';
import type { LabwarePropertiesForm, LabwareType } from '../../../types';
import { getColorFromIndex, labwareTypeToDisplayName } from '../../../utils';
import Chip from '../../ChipWFB';
import { ColorPickerPanel } from '../../ColorPickerPanel';
import { Icon } from '../../customIcons/labware';
import { TextFieldWithMaxLength } from '../../TextFieldWithMaxLength';

export type Props = {
  handlePendingChange: (update: Partial<LabwarePropertiesForm>) => void;
  handleSubmitForm: (override?: Partial<LabwarePropertiesForm>) => void;
  labwareColorIndex: number;
  labwareDefinitionURI: string;
  labwareType: LabwareType;
  nickname: string;
  setLabwareColor: (colorIndex: number) => void;
  workflowLabwareDefinitionURIs: string[];
};

const CUSTOM_LABWARE_PREFIX = 'custom/';

type LabwareOption = {
  label: string;
  value: string;
  isCustom?: boolean;
  isCreateAction?: boolean;
  isStaticDefinition?: boolean;
};

const filter = createFilterOptions<LabwareOption>();

const normalize = (value: string): string => value.trim().toLowerCase();

const isCustomLabwareValue = (value: string): boolean =>
  value.startsWith(CUSTOM_LABWARE_PREFIX);

const getCustomLabwareLabel = (value: string): string => {
  const maybeName = value.slice(CUSTOM_LABWARE_PREFIX.length);
  try {
    return decodeURIComponent(maybeName);
  } catch {
    return maybeName;
  }
};

const toCustomLabwareValue = (value: string): string =>
  `${CUSTOM_LABWARE_PREFIX}${encodeURIComponent(value)}`;

export const BaseLabwareSettings = ({
  handlePendingChange,
  handleSubmitForm,
  labwareColorIndex,
  labwareDefinitionURI,
  labwareType,
  nickname,
  setLabwareColor,
  workflowLabwareDefinitionURIs,
}: Props) => {
  const isDimensionOffsetsEnabled = useFlag('fe.dimension_offsets');
  const isCustomLabwareTypeEnabled = useFlag('fe.custom_labware_type');
  const labwareDefs = getAllLabwareDefinitionsOfType(labwareType);

  const [showColorPickerPanel, setShowColorPickerPanel] =
    useState<boolean>(false);

  const handleSelectColor = useCallback(
    (colorIndex: number) => {
      setShowColorPickerPanel(false);
      setLabwareColor(colorIndex);
    },
    [setLabwareColor]
  );

  const labwareHtmlColor = getColorFromIndex(labwareColorIndex);

  const labwareOptions = useMemo(() => {
    const staticOptions: LabwareOption[] = labwareDefs.map((def) => ({
      label: def.displayName,
      value: getLabwareURI(def),
      isStaticDefinition: true,
    }));

    const staticValues = new Set(staticOptions.map((option) => option.value));

    const customOptions: LabwareOption[] = workflowLabwareDefinitionURIs
      .filter(
        (value) => isCustomLabwareValue(value) && !staticValues.has(value)
      )
      .map((value) => ({
        label: getCustomLabwareLabel(value),
        value,
        isCustom: true,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    return [...staticOptions, ...customOptions];
  }, [labwareDefs, workflowLabwareDefinitionURIs]);

  const labwareValue = useMemo(
    () =>
      labwareOptions.find((option) => option.value === labwareDefinitionURI),
    [labwareOptions, labwareDefinitionURI]
  );

  return (
    <Stack gap={1}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="center"
        gap="4rem"
      >
        <Icon
          iconType={labwareType}
          fontSize="large"
          sx={{ color: 'grey.800' }}
        />
        <Chip label={labwareTypeToDisplayName(labwareType)} />
        <CircleIcon
          htmlColor={labwareHtmlColor}
          onClick={() => setShowColorPickerPanel(true)}
          fontSize="large"
        />
        {showColorPickerPanel ? (
          <ColorPickerPanel
            selectedColorIndex={labwareColorIndex}
            handleSelectColor={handleSelectColor}
            handleClickOutside={() => setShowColorPickerPanel(false)}
          />
        ) : null}
      </Stack>

      <FormControl>
        <Autocomplete
          options={labwareOptions}
          value={labwareValue || null}
          filterOptions={(options, params) => {
            const filtered = filter(options, params);
            const trimmedValue = params.inputValue.trim();
            if (trimmedValue === '') return filtered;

            const inputNormalized = normalize(trimmedValue);
            const exists = options.some(
              (option) =>
                normalize(option.label) === inputNormalized ||
                normalize(option.value) === inputNormalized
            );

            if (!exists && isCustomLabwareTypeEnabled) {
              filtered.unshift({
                label: `+ Add "${trimmedValue}" to ${labwareTypeToDisplayName(
                  labwareType
                )} library`,
                value: toCustomLabwareValue(trimmedValue),
                isCustom: true,
                isCreateAction: true,
              });
            }

            return filtered;
          }}
          onChange={(_, option) => {
            const nextValue = option?.value || '';
            handleSubmitForm({ labwareDefinitionURI: nextValue });
          }}
          onBlur={() => handleSubmitForm()}
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          renderInput={(inputProps) => (
            <TextField
              {...inputProps}
              variant="outlined"
              label="Labware"
              placeholder="Select labware"
            />
          )}
        />
      </FormControl>

      <TextFieldWithMaxLength
        label="Name"
        value={nickname}
        onChange={(e) => handlePendingChange({ nickname: e.target.value })}
        maxLength={25}
      />

      {labwareValue && isDimensionOffsetsEnabled && (
        <GrippingDimensions labwareDefinitionURI={labwareDefinitionURI} />
      )}
    </Stack>
  );
};
